import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import {
  getUser,
  createPermissionRequest,
  getEmployeePermissions,
  updatePermissionRequest,
  cancelPermissionRequest,
} from "../../services/apiService";
import LoadingButton from "../LoadingButton/LoadingButton";
import { useNotifications } from "../../components/NotificationContext/NotificationContext";
import "./EmployeeLeaveRequest.css";

const EmployeeLeaveRequest = () => {
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    permission_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [editFormData, setEditFormData] = useState({
    permission_id: "",
    permission_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const userInfo = getUser();
  const userId = userInfo?.user_id;

  useEffect(() => {
    if (userId) {
      fetchPermissions();
    } else {
      setApiError("Kullanıcı bilgileri bulunamadı");
      setLoading(false);
    }
  }, [userId]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const data = await getEmployeePermissions(userId);
      setPermissions(data || []);
      setApiError(null);
      setLoading(false);
    } catch (error) {
      console.error("İzinler yüklenemedi:", error.message);
      setApiError("İzinler yüklenemedi: " + error.message);
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      let dataToSend = {
        ...formData,
        user_id: userId,
      };
      if (
        formData.permission_type === "Diğer" &&
        formData.custom_permission_type
      ) {
        dataToSend.permission_type = formData.custom_permission_type;
        delete dataToSend.custom_permission_type;
      }
      const result = await createPermissionRequest(dataToSend);

      // YENİ İZİN TALEBİ BİLDİRİMİ - ADMINLERE GİDER
      addNotification({
        type: "permission",
        icon: "fa-user-clock",
        title: "Yeni İzin Talebi",
        message: `${
          userInfo?.name || userInfo?.email || "Bir çalışan"
        } izin talebinde bulundu`,
        targetRole: "admin",
        route: "/admin/permissions",
      });

      setNotification({
        show: true,
        message: "İzin talebiniz başarıyla oluşturuldu",
        type: "success",
      });
      setShowPermissionModal(false);
      fetchPermissions();
      setFormData({
        permission_type: "",
        start_date: "",
        end_date: "",
        reason: "",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "İzin talebi oluşturulamadı: " + error.message,
        type: "error",
      });
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditFormLoading(true);
    try {
      const dataToSend = {
        ...editFormData,
      };
      if (
        editFormData.permission_type === "Diğer" &&
        editFormData.custom_permission_type
      ) {
        dataToSend.permission_type = editFormData.custom_permission_type;
        delete dataToSend.custom_permission_type;
      }
      const result = await updatePermissionRequest(dataToSend);

      // İZİN GÜNCELLEME BİLDİRİMİ - ADMINLERE GİDER
      addNotification({
        type: "permission_update",
        icon: "fa-edit",
        title: "İzin Talebi Güncellendi",
        message: `${
          userInfo?.name || userInfo?.email || "Bir çalışan"
        } izin talebini güncelledi`,
        targetRole: "admin",
        route: "/admin/permissions",
      });

      setNotification({
        show: true,
        message: "İzin talebiniz başarıyla güncellendi",
        type: "success",
      });
      setShowEditModal(false);
      fetchPermissions();
    } catch (error) {
      setNotification({
        show: true,
        message: "İzin talebi güncellenemedi: " + error.message,
        type: "error",
      });
    } finally {
      setEditFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const handleCancelRequest = async (permissionId) => {
    if (
      !window.confirm("İzin talebinizi iptal etmek istediğinize emin misiniz?")
    ) {
      return;
    }
    try {
      await cancelPermissionRequest(permissionId);

      // İZİN İPTAL BİLDİRİMİ - ADMINLERE GİDER
      addNotification({
        type: "permission_canceled",
        icon: "fa-times-circle",
        title: "İzin Talebi İptal Edildi",
        message: `${
          userInfo?.name || userInfo?.email || "Bir çalışan"
        } izin talebini iptal etti`,
        targetRole: "admin",
        route: "/admin/permissions",
      });

      setNotification({
        show: true,
        message: "İzin talebiniz başarıyla iptal edildi",
        type: "success",
      });
      fetchPermissions();
    } catch (error) {
      setNotification({
        show: true,
        message: "İzin talebi iptal edilemedi: " + error.message,
        type: "error",
      });
    } finally {
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const openEditModal = (permission) => {
    setEditFormData({
      permission_id: permission.permission_id,
      permission_type: permission.permission_type,
      start_date: permission.start_date,
      end_date: permission.end_date,
      reason: permission.reason,
    });
    setShowEditModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: "pending", text: "Bekliyor" },
      approved: { class: "approved", text: "Onaylandı" },
      rejected: { class: "rejected", text: "Reddedildi" },
      canceled: { class: "canceled", text: "İptal Edildi" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge ${config.class}`}>{config.text}</span>
    );
  };

  if (apiError) {
    return (
      <div className="employee-leave-request">
        <div className="leave-request-container">
          <div className="error-message">
            <p>{apiError}</p>
            <button className="retry-button" onClick={() => fetchPermissions()}>
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="employee-leave-request">
      <div className="leave-request-container">
        <div className="page-header">
          <div className="header-content">
            <h2 className="page-title">İzin Taleplerim</h2>
            <div className="header-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowPermissionModal(true)}
              >
                <i className="fas fa-plus"></i> Yeni İzin Talebi
              </button>
            </div>
          </div>
        </div>

        {notification.show && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        <div className="leave-request-content">
          <div className="permissions-list">
            {permissions.length > 0 ? (
              permissions.map((permission) => (
                <div key={permission.permission_id} className="permission-item">
                  <div className="permission-info">
                    <div className="permission-type">
                      <strong>{permission.permission_type}</strong>
                    </div>
                    <div className="permission-dates">
                      {formatDate(permission.start_date)} -{" "}
                      {formatDate(permission.end_date)}
                    </div>
                    <div className="permission-reason">{permission.reason}</div>
                  </div>
                  <div className="permission-status">
                    {getStatusBadge(permission.status)}
                  </div>
                  <div className="permission-actions">
                    {permission.status === "pending" && (
                      <>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => openEditModal(permission)}
                          title="Düzenle"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="action-btn cancel-btn"
                          onClick={() =>
                            handleCancelRequest(permission.permission_id)
                          }
                          title="İptal Et"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-permissions">
                <i className="fas fa-calendar-times"></i>
                <p>Henüz izin talebiniz bulunmuyor</p>
              </div>
            )}
          </div>
        </div>

        {/* Yeni İzin Talebi Modal */}
        {showPermissionModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Yeni İzin Talebi</h3>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="close-button"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>İzin Türü</label>
                    <select
                      name="permission_type"
                      value={formData.permission_type}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Seçiniz</option>
                      <option value="Yıllık İzin">Yıllık İzin</option>
                      <option value="Hastalık İzni">Hastalık İzni</option>
                      <option value="Ücretsiz İzin">Ücretsiz İzin</option>
                      <option value="Evlilik İzni">Evlilik İzni</option>
                      <option value="Doğum İzni">Doğum İzni</option>
                      <option value="Ölüm İzni">Ölüm İzni</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  {formData.permission_type === "Diğer" && (
                    <div className="form-group">
                      <label>İzin Adı</label>
                      <input
                        type="text"
                        name="custom_permission_type"
                        value={formData.custom_permission_type || ""}
                        onChange={handleFormChange}
                        placeholder="Özel izin adını girin"
                      />
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Başlangıç Tarihi</label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Bitiş Tarihi</label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Açıklama</label>
                    <textarea
                      name="reason"
                      value={formData.reason}
                      onChange={handleFormChange}
                      rows="3"
                      required
                    ></textarea>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      onClick={() => setShowPermissionModal(false)}
                      className="btn btn-secondary"
                    >
                      İptal
                    </button>
                    <LoadingButton
                      type="submit"
                      className="btn btn-primary"
                      isLoading={formLoading}
                    >
                      Oluştur
                    </LoadingButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* İzin Düzenleme Modal */}
        {showEditModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>İzin Talebini Düzenle</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="close-button"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <input
                    type="hidden"
                    name="permission_id"
                    value={editFormData.permission_id}
                  />

                  <div className="form-group">
                    <label>İzin Türü</label>
                    <select
                      name="permission_type"
                      value={editFormData.permission_type}
                      onChange={handleEditFormChange}
                      required
                    >
                      <option value="">Seçiniz</option>
                      <option value="Yıllık İzin">Yıllık İzin</option>
                      <option value="Hastalık İzni">Hastalık İzni</option>
                      <option value="Ücretsiz İzin">Ücretsiz İzin</option>
                      <option value="Evlilik İzni">Evlilik İzni</option>
                      <option value="Doğum İzni">Doğum İzni</option>
                      <option value="Ölüm İzni">Ölüm İzni</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  {editFormData.permission_type === "Diğer" && (
                    <div className="form-group">
                      <label>İzin Adı</label>
                      <input
                        type="text"
                        name="custom_permission_type"
                        value={editFormData.custom_permission_type || ""}
                        onChange={handleEditFormChange}
                        placeholder="Özel izin adını girin"
                      />
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Başlangıç Tarihi</label>
                      <input
                        type="date"
                        name="start_date"
                        value={editFormData.start_date}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Bitiş Tarihi</label>
                      <input
                        type="date"
                        name="end_date"
                        value={editFormData.end_date}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Açıklama</label>
                    <textarea
                      name="reason"
                      value={editFormData.reason}
                      onChange={handleEditFormChange}
                      rows="3"
                      required
                    ></textarea>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn btn-secondary"
                    >
                      İptal
                    </button>
                    <LoadingButton
                      type="submit"
                      className="btn btn-primary"
                      isLoading={editFormLoading}
                    >
                      Güncelle
                    </LoadingButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeLeaveRequest;
